import { useContext } from 'react';

import {
  Skeleton,
  InfoTooltip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { formatCurrency } from '@cast/utils';

import { TendencyIndicator } from 'components/indicators';
import { Money } from 'components/money';
import { TrafficRatioBars } from 'features/cost-report/components/network-cost/TrafficRatioBars';
import { getNetworkCostPrecision } from 'features/cost-report/utils/networkCosts';

import { SummaryBox } from './_components/SummaryBox';
import { useCostReportContext } from '../../../../cost-report/hooks/useCostReportContext';
import { NetworkCostOverTimeContext } from '../../providers/NetworkCostOverTimeProvider';
import { useDataTransferSummary } from '../_hooks/useDataTransferSummary';

export const SummarySection = () => {
  const { isDefaultFilter } = useCostReportContext();
  const { dynamicCopiesMap, current } = useContext(NetworkCostOverTimeContext);
  const { cost, averageDailyCost, tendency } = useDataTransferSummary();

  const totalCost = cost.egress + cost.ingress;
  const totalCostPrecision = getNetworkCostPrecision(totalCost);

  const egressCostPrecision = getNetworkCostPrecision(cost.egress);
  const ingressCostPrecision = getNetworkCostPrecision(cost.ingress);

  const totalAverageDailyCost =
    averageDailyCost.egress + averageDailyCost.ingress;
  const totalAverageDailyCostPrecision = getNetworkCostPrecision(
    totalAverageDailyCost
  );

  const averageDailyCostEgressPrecision = getNetworkCostPrecision(
    averageDailyCost.egress
  );
  const averageDailyCostIngressPrecision = getNetworkCostPrecision(
    averageDailyCost.ingress
  );

  if (current.isLoading) {
    return (
      <Stack direction="row" gap={24}>
        <Skeleton width="100%" height={104} />
        <Skeleton width="100%" height={104} />
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={24} data-hc="success">
      <SummaryBox>
        <Stack gap={4}>
          <Typography
            variant="L12R"
            display="inline-flex"
            alignItems="center"
            gap={4}
          >
            {isDefaultFilter ? 'Current month spend' : 'Total spend'}{' '}
            <InfoTooltip title={dynamicCopiesMap.summary.totalSpendTooltip} />
          </Typography>
          <Typography variant="h2">
            <Money
              amount={totalCost}
              precision={totalCostPrecision}
              fixed={totalCostPrecision}
            />
          </Typography>
        </Stack>

        <Stack gap={10}>
          <Stack direction="row" justifyContent="space-between">
            <Stack gap={4}>
              <Typography variant="L12R" color="textSecondary">
                Egress
              </Typography>
              <Typography variant="h5">
                <Money
                  amount={cost.egress}
                  precision={egressCostPrecision}
                  fixed={egressCostPrecision}
                />
              </Typography>
            </Stack>

            <Stack gap={4} alignItems="flex-end">
              <Typography variant="L12R" color="textSecondary">
                Ingress
              </Typography>
              <Typography variant="h5">
                <Money
                  amount={cost.ingress}
                  precision={ingressCostPrecision}
                  fixed={ingressCostPrecision}
                />
              </Typography>
            </Stack>
          </Stack>
          <TrafficRatioBars egress={cost.egress} ingress={cost.ingress} />
        </Stack>
      </SummaryBox>
      <SummaryBox>
        <Stack gap={8}>
          <Stack gap={4}>
            <Typography
              variant="L12R"
              display="inline-flex"
              alignItems="center"
              gap={4}
            >
              avg. daily total cost{' '}
              <InfoTooltip
                title={dynamicCopiesMap.summary.averageTotalDailyCost}
              />
            </Typography>
            <Typography variant="h5">
              <Money
                amount={totalAverageDailyCost}
                precision={totalAverageDailyCostPrecision}
                fixed={totalAverageDailyCostPrecision}
              />
            </Typography>
          </Stack>

          {tendency.display && (
            <Stack direction="row" gap={6} data-testid="tendency-badge">
              <TendencyIndicator
                variant="arrow"
                tendency={tendency.direction}
                value={formatCurrency(tendency.diff, {
                  maximumFractionDigits: 2,
                }).toString()}
                sx={{ height: 16 }}
                size={10}
                valueProps={{ variant: 'L10M' }}
              />

              <Typography
                variant="P10R"
                component="span"
                color={tokens.theme.fg.structural.secondary}
              >
                Than last month
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack gap={10}>
          <Stack direction="row" justifyContent="space-between">
            <Stack gap={4}>
              <Typography
                variant="L12R"
                display="inline-flex"
                alignItems="center"
                gap={4}
              >
                avg. daily cost{' '}
                <InfoTooltip
                  title={dynamicCopiesMap.summary.averageDailyCost}
                />
              </Typography>

              <Stack gap={4}>
                <Typography variant="h5" lineHeight={24}>
                  <Money
                    amount={averageDailyCost.egress}
                    precision={averageDailyCostEgressPrecision}
                    fixed={averageDailyCostEgressPrecision}
                  />{' '}
                  <Typography
                    variant="L10R"
                    component="span"
                    color="textSecondary"
                  >
                    /GiB egress
                  </Typography>
                </Typography>

                <Typography variant="h5" lineHeight={24}>
                  <Money
                    amount={averageDailyCost.ingress}
                    precision={averageDailyCostIngressPrecision}
                    fixed={averageDailyCostIngressPrecision}
                  />{' '}
                  <Typography
                    variant="L10R"
                    component="span"
                    color="textSecondary"
                  >
                    /GiB ingress
                  </Typography>
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </SummaryBox>
    </Stack>
  );
};
